/**
 * LES JOURS D'UNE INSCRIPTION — lesquels sont retenus, et comment ils s'écrivent.
 *
 * ⚠️ CE FICHIER EXISTE POUR LA PREMIÈRE RÈGLE, qui est contre-intuitive et coûteuse à
 * se tromper : NE PAS POSER LA QUESTION NE VEUT PAS DIRE « AUCUN JOUR ». Un événement
 * d'un seul jour, ou dont l'organisateur a retiré l'étape, inscrit le participant à
 * TOUS ses jours — c'est ce que fait la V1 en sautant son écran
 * (`setDaysId2(event.days.map(m => m.id))` dans `screens/steps/Dates.jsx`), et il n'y
 * a pas d'autre choix raisonnable : sans jour retenu, le serveur recalcule
 * `UserEvent.isRegistered` à `false` et le participant se présente à la porte avec une
 * inscription qui n'en est pas une.
 *
 * ⚠️ CE RECALCUL EST LA SECONDE RAISON D'ÊTRE ATTENTIF ICI. Écrire un jour déclenche
 * `updateRegisteredPresent` côté serveur, qui REDÉFINIT `isRegistered` à partir des
 * jours retenus — chez le produit, « inscrit » VEUT DIRE « inscrit à au moins un
 * jour ». Ce que cette fonction rend doit donc s'accorder avec ce que la confirmation
 * écrit par ailleurs : quelqu'un qui vient garde au moins un jour, quelqu'un qui
 * décline n'en garde aucun. Voir `funnel/participant.ts`, qui les écrit dans cet ordre.
 */

/**
 * LES JOURS À INSCRIRE, dans l'ordre de préséance du tunnel : ce qu'on répond
 * maintenant, ce que le serveur détient déjà, puis le défaut.
 *
 * ⚠️ LE MILIEU N'EST PAS DÉCORATIF. Quelqu'un qui revient sur une inscription
 * enregistrée peut atteindre la confirmation par le fil d'Ariane sans repasser par
 * l'étape des dates : sans ce niveau, on lui réinscrirait TOUS les jours par-dessus le
 * seul qu'il avait choisi.
 */
export function selectedDaysOf({
  coming,
  chosen,
  saved,
  all,
}: {
  /** Le participant vient — sur place ou à distance. */
  coming: boolean;
  /** Ce qu'il vient de cocher, s'il a traversé l'étape. */
  chosen?: ReadonlyArray<number>;
  /** Ce que le serveur détient déjà. */
  saved?: ReadonlyArray<number>;
  /** Tous les jours de l'événement — le défaut, et le filtre. */
  all: ReadonlyArray<number>;
}): number[] {
  // Ne vient pas : aucun jour. C'est ce qui fait que le serveur retombe sur
  // « non inscrit », en accord avec ce que la confirmation écrit à côté.
  if (!coming) return [];

  /* ⚠️ FILTRÉS CONTRE LES JOURS RÉELS DE L'ÉVÉNEMENT. Les choix viennent de
     `history.state` — donc de quelque chose qu'un autre onglet a pu écrire et qu'on
     peut composer à la main (voir `funnel/answers.ts`). Un identifiant qui n'est pas
     un jour d'ici partirait vers une route qui répond « Ce jour n'existe pas sur cet
     événement », et l'inscription échouerait en bloc. */
  const known = (list: ReadonlyArray<number>) =>
    all.filter((id) => list.includes(id));

  const ticked = chosen ? known(chosen) : [];
  if (ticked.length > 0) return ticked;

  const previous = saved ? known(saved) : [];
  if (previous.length > 0) return previous;

  return [...all];
}

/**
 * L'ÉTAPE DES DATES EST-ELLE RÉPONDUE ? — la question que pose « Continuer », posée ici
 * pour que le fil d'Ariane pose LA MÊME (bug #257).
 *
 * ⚠️ LA RÈGLE « AU MOINS UN JOUR » NE VIVAIT QUE SUR LE BOUTON. Le fil ouvrait les
 * étapes suivantes sans la consulter : tout décocher, cliquer « Confirmation » dans le
 * fil, et l'écran annonçait « Votre inscription est enregistrée » en listant le jour
 * qu'on venait de décocher — `selectedDaysOf` lit une liste vide comme une absence de
 * réponse et retombe sur ce que le serveur détient.
 *
 * ⚠️ MÊME ORDRE DE LECTURE QUE L'ÉCRAN : ce qu'on vient de cocher, sinon ce que le
 * serveur détient. Une liste VIDE cochée à l'instant l'emporte donc sur le serveur —
 * c'est tout l'objet : elle dit « j'ai tout décoché », pas « je n'ai pas répondu ».
 */
export function datesAnswered({
  chosen,
  saved,
}: {
  /** Ce que le participant vient de cocher, s'il a touché à l'étape. */
  chosen?: ReadonlyArray<number>;
  /** Ce que le serveur détient. */
  saved?: ReadonlyArray<number>;
}): boolean {
  return (chosen ?? saved ?? []).length > 0;
}

/**
 * UN JOUR, ÉCRIT COMME LA V1 L'ÉCRIT : « Lundi 21 septembre ».
 *
 * ⚠️ MIDI EN UTC, ET PAS MINUIT. La base rend `AAAA-MM-JJ` ; lu comme minuit UTC dans
 * un fuseau à l'ouest, le 21 devient le 20 au soir — et le participant coche un jour
 * qui n'est pas celui qu'il croit. Le milieu de journée est à l'abri des deux côtés.
 *
 * ⚠️ PAS D'ANNÉE, comme la V1 : on est dans le tunnel d'un événement dont la période
 * est déjà annoncée en tête de site. Le jour de la semaine, lui, reste — c'est ce qu'on
 * lit pour savoir si on peut se libérer.
 */
export function longDay(day: string, locale: string): string {
  const date = new Date(`${day}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return day;

  const options: Intl.DateTimeFormatOptions = {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  };
  /* La langue vient de la base (« FR », « EN »…). `Intl` lève sur une étiquette qu'il
     ne sait pas lire : on retombe alors sur celle du navigateur plutôt que de faire
     tomber l'écran. */
  let format: Intl.DateTimeFormat;
  try {
    format = new Intl.DateTimeFormat(locale, options);
  } catch {
    format = new Intl.DateTimeFormat(undefined, options);
  }

  const written = format.format(date);
  /**
   * La majuscule initiale de la V1 (`capitalizeFirstLetter`). Deux précautions, et la
   * seconde a été trouvée par son test :
   *
   *   `[...ecrit]` et non `ecrit[0]` — une première lettre accentuée composée se
   *   couperait en deux.
   *
   *   ⚠️ LA LANGUE *RÉSOLUE*, pas celle qu'on a demandée. `toLocaleUpperCase` lève sur
   *   une étiquette qu'il ne sait pas lire, exactement comme `DateTimeFormat` — le
   *   `try` du dessus ne protégeait donc que la moitié du chemin, et une clé de langue
   *   exotique faisait tomber l'écran APRÈS avoir formaté la date. Celle que le
   *   formateur a retenue est valide par construction.
   */
  const [first, ...remaining] = [...written];
  const selected = format.resolvedOptions().locale;
  return first ? first.toLocaleUpperCase(selected) + remaining.join("") : written;
}
